import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Tabs, TabList, Tab,
  Dropdown,
  DataTable,
  Table, TableHead, TableRow, TableHeader, TableBody, TableCell,
  TableContainer,
  Tag,
  InlineLoading,
  Pagination,          // 👈 added
} from "@carbon/react";

import { callMongoApi } from "../../../services/mongoApiService";

const DATE_RANGE_OPTIONS = [
  { id: "today", text: "Today" },
  { id: "yesterday", text: "Yesterday" },
  { id: "last7days", text: "Last 7 Days" },
  { id: "last15days", text: "Last 15 Days" },
  { id: "last30days", text: "Last 30 Days" },
];

const PLATFORM_OPTIONS = [
  { id: "all", text: "Combined Views" },
  { id: "web", text: "Website Views" },
  { id: "mobile", text: "Mobile Views" },
];

const VIEWS_HEADERS = [
  { key: "name", header: "User" },
  { key: "userId", header: "User ID" },
  { key: "viewedOn", header: "Platform" },
  { key: "viewTime", header: "Viewed At" },
];

const UNIQUE_VIEWERS_HEADERS = [
  { key: "name", header: "User" },
  { key: "userId", header: "User ID" },
  { key: "views", header: "Views" },
  { key: "devices", header: "Devices" },
  { key: "firstActivity", header: "First Activity" },
  { key: "lastActivity", header: "Last Activity" },
];

const SHORTLIST_HEADERS = [
  { key: "name", header: "User" },
  //{ key: "userId", header: "User ID" },
  { key: "shortlistedOn", header: "Platform" },
  { key: "shortlistTime", header: "Shortlisted At" },
];

const CONTACT_HEADERS = [
  { key: "name", header: "User" },
  { key: "userId", header: "User ID" },
   { key: "contact", header: "Contact Number" },  
  { key: "contactedOn", header: "Platform" },
  { key: "contactTime", header: "Contacted At" },
];

// Same idea as PropertyTable's page sizes
const PAGE_SIZES = [10, 20, 30, 50];

const formatDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return isNaN(d) ? "-" : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

export default function PropertyAnalytics() {
  const { propertyUuid } = useParams();
  const location = useLocation();
  const propertyTitle = location.state?.propertyTitle || "";

  const [dateRange, setDateRange] = useState(DATE_RANGE_OPTIONS[4]);
  const [platform, setPlatform] = useState(PLATFORM_OPTIONS[0]);
  const [activeTab, setActiveTab] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 👇 Pagination state — one page/pageSize per tab, mirroring Property.js's
  //    "reset page on data change" behaviour but scoped per tab.
  const [pageState, setPageState] = useState([
    { page: 1, pageSize: 10 },
    { page: 1, pageSize: 10 },
    { page: 1, pageSize: 10 },
    { page: 1, pageSize: 10 }, 
  ]);

  const updatePageState = (tabIndex, patch) => {
    setPageState((prev) =>
      prev.map((p, i) => (i === tabIndex ? { ...p, ...patch } : p))
    );
  };

  // Reset pagination whenever the dataset changes (dateRange / platform / property)
const resetPagination = () => {
  setPageState([
    { page: 1, pageSize: 10 },
    { page: 1, pageSize: 10 },
    { page: 1, pageSize: 10 },
    { page: 1, pageSize: 10 },
  ]);
};

  const fetchAnalytics = useCallback(async () => {
    if (!propertyUuid) return;
    setLoading(true);
    setError("");
    try {
      const endpoint = `/properties/${propertyUuid}/analytics?dateRange=${dateRange.id}&platform=${platform.id}`;
      const response = await callMongoApi("GET", endpoint);
      if (response?.success && response?.data) {
        setAnalytics(response.data);
        resetPagination();               // 👈 same idea as setPage(1)
      } else {
        setAnalytics(null);
        setError("No analytics data available for this property.");
      }
    } catch (err) {
      console.error("Failed to fetch property analytics:", err);
      setError("Failed to load analytics. Please try again.");
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [propertyUuid, dateRange, platform]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const viewsRows = useMemo(
    () =>
      (analytics?.views?.list || []).map((v, i) => ({
        id: `view-${i}`,
        name: v.name || "N/P",
        userId: v.userId || "-",
        viewedOn: v.viewedOn || "-",
        viewTime: formatDateTime(v.viewTime),
      })),
    [analytics]
  );

  const uniqueViewerRows = useMemo(
  () =>
    (analytics?.uniqueUserActivity || []).map((u, i) => ({
      id: `unique-${i}`,
      name: u.name || "N/P",
      userId: u.userId || "-",
      views: u.views ?? 0,
      devices: (u.devices || []).join(", ") || "-",
      firstActivity: formatDateTime(u.firstActivity),
      lastActivity: formatDateTime(u.lastActivity),
    })),
  [analytics]
);

  const shortlistRows = useMemo(
    () =>
      (analytics?.shortlists?.list || []).map((s, i) => ({
        id: `shortlist-${i}`,
        name: s.name || "N/P",
       // userId: s.userId || "-",
        shortlistedOn: s.shortlistedOn || "-",
        shortlistTime: formatDateTime(s.shortlistTime),
      })),
    [analytics]
  );

const contactRows = useMemo(
  () =>
    (analytics?.contacts?.list || []).map((c, i) => ({
      id: `contact-${i}`,
      name: c.name || "N/P",
      userId: c.userId || "-",
      contact: c.contact || "-",                                  // 👈 added
      contactedOn: c.contactedOn || c.viewedOn || "-",
      contactTime: formatDateTime(c.contactedTime || c.contactTime || c.viewTime), // 👈 fixed key
    })),
  [analytics]
);

  // 👇 Mirrors Property.js: parent slices, passes slice + metadata down
  const renderTable = (headers, allRows, emptyLabel, tabIndex) => {
    const { page, pageSize } = pageState[tabIndex];

    // Safety clamp (Property.js doesn't do this, but it's cheap insurance
    // if the analytics payload shrinks between fetches)
    const totalItems = allRows.length;
    const maxPage = Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage = Math.min(page, maxPage);

    const startIndex = (safePage - 1) * pageSize;
    const paginatedRows = allRows.slice(startIndex, startIndex + pageSize);

    return (
      <>
        <DataTable rows={paginatedRows} headers={headers}>
          {({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
            <TableContainer>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map((h) => (
                      <TableHeader {...getHeaderProps({ header: h })} key={h.key}>
                        {h.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length} style={{ textAlign: "center" }}>
                        {emptyLabel}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow {...getRowProps({ row })} key={row.id}>
                        {row.cells.map((cell) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>

        {/* 👇 Carbon Pagination — same component PropertyTable uses under the hood */}
        {totalItems > 0 && (
          <Pagination
            totalItems={totalItems}
            page={safePage}
            pageSize={pageSize}
            pageSizes={PAGE_SIZES}
            itemsPerPageText="Items per page:"
            onChange={({ page: newPage, pageSize: newPageSize }) => {
              updatePageState(tabIndex, {
                // If user changed page size, Carbon already recomputes page,
                // so trust newPage in both cases.
                page: newPage,
                pageSize: newPageSize,
              });
            }}
          />
        )}
      </>
    );
  };

  return (
    <div style={{ padding: "1rem 2rem" }}>
      <h2 style={{ marginBottom: "1.5rem" }}>
        Property Analytics{propertyTitle ? ` — ${propertyTitle}` : ""}
      </h2>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <Dropdown
          id="analytics-date-range"
          titleText="Date Range"
          label="Select date range"
          items={DATE_RANGE_OPTIONS}
          itemToString={(item) => (item ? item.text : "")}
          selectedItem={dateRange}
          onChange={({ selectedItem }) => setDateRange(selectedItem)}
        />
        <Dropdown
          id="analytics-platform"
          titleText="Views"
          label="Select views"
          items={PLATFORM_OPTIONS}
          itemToString={(item) => (item ? item.text : "")}
          selectedItem={platform}
          onChange={({ selectedItem }) => setPlatform(selectedItem)}
        />
      </div>

      {loading && <InlineLoading description="Loading analytics..." />}
      {error && !loading && <p style={{ color: "#da1e28" }}>{error}</p>}

      {!loading && !error && analytics && (
        <>
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
            <Tag type="blue">Views: {analytics.views?.total ?? 0}</Tag>
            <Tag type="purple">Unique Viewers: {analytics.views?.uniqueViewers ?? 0}</Tag>
            <Tag type="green">Shortlists: {analytics.shortlists?.total ?? 0}</Tag>
            <Tag type="teal">Contacts: {analytics.contacts?.total ?? 0}</Tag>
          </div>

          <Tabs selectedIndex={activeTab} onChange={({ selectedIndex }) => setActiveTab(selectedIndex)}>
<TabList aria-label="Analytics tabs">
  <Tab>Views ({viewsRows.length})</Tab>
  <Tab>Unique Viewers ({uniqueViewerRows.length})</Tab>  
  <Tab>Shortlists ({shortlistRows.length})</Tab>
  <Tab>Contacted ({contactRows.length})</Tab> 
</TabList>
          </Tabs>

          <div style={{ marginTop: "1rem" }}>
            {activeTab === 0 && renderTable(VIEWS_HEADERS, viewsRows, "No views in this period.", 0)}
            {activeTab === 1 && renderTable(UNIQUE_VIEWERS_HEADERS, uniqueViewerRows, "No unique viewers in this period.", 3)} 
            {activeTab === 2 && renderTable(SHORTLIST_HEADERS, shortlistRows, "No shortlists in this period.", 1)}
            {activeTab === 3 && renderTable(CONTACT_HEADERS, contactRows, "No contacts in this period.", 2)}
          </div>
        </>
      )}
    </div>
  );
}